import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { build, createItem, type TimelineItem } from '@easyedit/timeline'
import { api, uploadToSignedUrl } from '../../../lib/api/client'
import type { MediaAsset, Project } from '../../../lib/api/types'
import { useEditorStore } from '../../../stores/editorStore'

interface UploadInit {
  assetId: string
  mode: 'single' | 'multipart'
  uploadUrl?: string
  uploadId?: string
  key: string
  partSize?: number
  parts?: Array<{ partNumber: number; url: string; size: number }>
}

export function MediaPanel({ project, assets }: { project: Project; assets: MediaAsset[] }) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploads, setUploads] = useState<Record<string, number>>({})
  const [dragOver, setDragOver] = useState(false)
  const sequence = useEditorStore((s) => s.sequence)
  const dispatch = useEditorStore((s) => s.dispatch)
  const playhead = useEditorStore((s) => s.playhead)

  async function upload(files: FileList | File[]) {
    for (const file of Array.from(files)) {
      const key = `${file.name}-${file.size}`
      setUploads((prev) => ({ ...prev, [key]: 0 }))
      try {
        const init = await api.post<UploadInit>('/media/upload-init', {
          projectId: project.id,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        })

        if (init.mode === 'single' && init.uploadUrl) {
          await uploadToSignedUrl(init.uploadUrl, file, file.type, (fraction) =>
            setUploads((prev) => ({ ...prev, [key]: fraction })),
          )
          await api.post(`/media/${init.assetId}/upload-complete`, {})
        } else if (init.parts) {
          // Multipart: cada parte sobe separadamente e pode ser reenviada sozinha se
          // falhar — reenviar 8 MB é aceitável, reenviar 4 GB não.
          const done: Array<{ partNumber: number; etag: string }> = []
          for (const part of init.parts) {
            const offset = (part.partNumber - 1) * (init.partSize ?? 0)
            const chunk = file.slice(offset, offset + part.size)
            const partEtag = await uploadToSignedUrl(
              part.url,
              chunk,
              'application/octet-stream',
              (fraction) => {
                const overall = (part.partNumber - 1 + fraction) / init.parts!.length
                setUploads((prev) => ({ ...prev, [key]: overall }))
              },
              true,
            )
            // O S3/MinIO exige o ETag de cada parte para fechar o multipart. Num upload
            // cross-origin o navegador só deixa ler esse header se o storage responder
            // com `Access-Control-Expose-Headers: ETag` — sem isso vem vazio, e o erro
            // apareceria lá no fundo do SDK da AWS, ilegível. Falhar aqui diz o que fazer.
            if (!partEtag) {
              throw new Error(
                'O storage não expôs o header ETag. Configure ' +
                  'Access-Control-Expose-Headers: ETag no MinIO/S3 para uploads grandes.',
              )
            }
            done.push({ partNumber: part.partNumber, etag: partEtag })
          }
          await api.post(`/media/${init.assetId}/upload-complete`, { parts: done })
        }

        setUploads((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
        void queryClient.invalidateQueries({ queryKey: ['media', project.id] })
      } catch (error) {
        useEditorStore.getState().setError(
          error instanceof Error ? `Falha no upload de ${file.name}: ${error.message}` : 'Falha no upload',
        )
        setUploads((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
      }
    }
  }

  function addToTimeline(asset: MediaAsset) {
    if (!sequence || asset.status !== 'READY') return
    const kind = asset.kind === 'VIDEO' ? 'video' : asset.kind === 'AUDIO' ? 'audio' : 'image'
    // Imagem não tem duração própria; 5s é o padrão de mercado e é ajustável no trim.
    const duration = asset.durationSec ?? 5
    const trackKind = kind === 'audio' ? 'audio' : kind === 'image' ? 'video' : 'video'
    const track = sequence.tracks.find((t) => t.kind === trackKind && !t.locked)
    if (!track) {
      useEditorStore.getState().setError(`Nenhuma track de ${trackKind} disponível`)
      return
    }

    const item: TimelineItem = createItem({
      kind,
      start: playhead,
      duration,
      source: { assetId: asset.id, in: 0, out: duration },
      label: asset.filename,
    })
    void dispatch(build.addItem({ sequenceId: sequence.id }, track.id, item))
  }

  return (
    <div className="p-3 flex flex-col gap-3">
      <div
        className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors cursor-pointer
          ${dragOver ? 'border-accent bg-accent-soft/40' : 'border-ink-700 hover:border-ink-600'}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          if (e.dataTransfer.files.length) void upload(e.dataTransfer.files)
        }}
      >
        <p className="text-xs text-ink-300">Arraste arquivos ou clique</p>
        <p className="mt-1 text-2xs text-ink-500">MP4, MOV, WebM, MKV, MP3, WAV, JPG, PNG</p>
        <input
          id="media-file-input"
          ref={inputRef}
          type="file"
          multiple
          accept="video/*,audio/*,image/*"
          className="hidden"
          onChange={(e) => e.target.files && void upload(e.target.files)}
        />
      </div>

      {Object.entries(uploads).map(([key, progress]) => (
        <div key={key} className="text-2xs text-ink-400">
          <div className="flex justify-between mb-1">
            <span className="truncate">{key.split('-')[0]}</span>
            <span className="tnum">{Math.round(progress * 100)}%</span>
          </div>
          <div className="h-1 bg-ink-750 rounded overflow-hidden">
            <div className="h-full bg-accent transition-[width]" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
      ))}

      <div className="flex flex-col gap-1.5">
        {assets.length === 0 && (
          <p className="text-2xs text-ink-500 text-center py-4">Nenhuma mídia ainda.</p>
        )}
        {assets.map((asset) => (
          <AssetRow key={asset.id} asset={asset} onAdd={() => addToTimeline(asset)} />
        ))}
      </div>
    </div>
  )
}

const STATUS_TEXT: Record<string, string> = {
  UPLOADING: 'enviando…',
  UPLOADED: 'na fila',
  PROCESSING: 'processando…',
  READY: '',
  FAILED: 'falhou',
}

function AssetRow({ asset, onAdd }: { asset: MediaAsset; onAdd: () => void }) {
  const ready = asset.status === 'READY'
  return (
    <button
      onClick={onAdd}
      disabled={!ready}
      className={`w-full text-left rounded border p-2 flex gap-2 items-center transition-colors
        ${ready ? 'border-ink-750 hover:border-ink-600 hover:bg-ink-800' : 'border-ink-800 opacity-70 cursor-default'}`}
      title={ready ? 'Adicionar no playhead' : STATUS_TEXT[asset.status]}
    >
      <div className="w-12 h-8 rounded bg-ink-900 shrink-0 overflow-hidden grid place-items-center">
        {asset.urls?.poster ? (
          <img src={asset.urls.poster} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-2xs text-ink-500">
            {asset.kind === 'AUDIO' ? '♪' : asset.kind === 'IMAGE' ? '▣' : '▶'}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-2xs text-ink-200 truncate">{asset.filename}</div>
        <div className="font-mono text-[9px] text-ink-500 tnum">
          {asset.durationSec ? `${asset.durationSec.toFixed(1)}s` : ''}
          {asset.width ? ` · ${asset.width}×${asset.height}` : ''}
          {!ready && ` · ${STATUS_TEXT[asset.status]}`}
        </div>
        {asset.status === 'FAILED' && asset.errorMessage && (
          <div className="text-[9px] text-danger truncate">{asset.errorMessage}</div>
        )}
      </div>
    </button>
  )
}
